import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { safeStorage } from '../lib/safeStorage'
import { featureKeyForPath } from '../lib/featureFlags'
import { Avatar } from './Avatar'
import { MoonIcon } from './MoonIcon'
import { Wordmark } from './Wordmark'
import { NextEpLogo } from './NextEpLogo'
import { Home as HomeIcon, Compass, ListChecks, Users, Puzzle, Target, Gavel, BarChart3, User as UserIcon, ChevronsLeft, ChevronsRight, MessagesSquare, Bell, Clock, Download, CircleHelp, Settings as SettingsIcon } from 'lucide-react'

// ── DesktopShell ─────────────────────────────────────────────────────────────
// The persistent "control booth" chrome for wide (≥720px) authenticated pages —
// a left nav rail (replaces BottomNav on desktop) and a bottom activity ticker.
// Approved via Claude Design mockups (2026-08-15): mobile is "in front of the
// camera," desktop is "the booth behind the glass." Always mounted (cheap —
// static nav + one lightweight ticker query) but invisible via CSS unless the
// current route rendered `surface-wide` on its root (see index.css's DESKTOP
// CONTROL BOOTH block) — so pages that haven't opted in yet are byte-identical
// to before, and mobile never sees any of this.
//
// NAV: array order IS the CH numbering (01, 02, 03…), matching what Design
// numbered in the mockup. Keep in sync with BottomNav's route set where they
// overlap; this is additive, not a replacement for BottomNav (which still
// renders — hidden by the same CSS — for markup parity across widths).
const NAV = [
  { to: '/',            label: 'Home',       icon: HomeIcon },
  { to: '/discover',    label: 'Discover',   icon: Compass },
  { to: '/list',        label: 'My List',    icon: ListChecks },
  { to: '/tonight',     label: 'Tonight',    icon: MoonIcon, countKey: 'tonight' },
  { to: '/people',      label: 'People',     icon: Users },
  { to: '/cliffhanger', label: 'Cliffhanger', icon: Puzzle, dotKey: 'cliffhanger' },
  { to: '/faceoff',     label: 'Face-Off',   icon: Target, dotKey: 'faceoff' },
  { to: '/verdict',     label: 'Verdict',    icon: Gavel, dotKey: 'verdict' },
  { to: '/stats',       label: 'Stats',      icon: BarChart3 },
  { to: '/profile',     label: 'Profile',    icon: UserIcon },
]

// The rest of BottomNav's "More" hub (HUB_CATEGORIES) that the primary 9 above
// don't already cover — the desktop rail launched with only the bottom-nav's own
// 9 items and silently dropped everything the mobile hub surfaces (Aftershow,
// Canon, Shelves, Buzz, Reviews, Notifications, History, Cards, Import, Help;
// For You too). Desktop has the vertical room BottomNav's hub sheet exists to
// work around, so these render inline as a second, unlabeled-CH group instead of
// needing their own drawer (Jason 2026-08-15: "we seem to be missing a bit").
// Admin is deliberately excluded (internal-only, not in the mobile hub either).
// Icons here match the exact lucide glyphs BottomNav's own hub sheet uses for
// the same destinations (2026-09-16 hub-icon fix, "reeks of v16 because of the
// icons") — this section imported the OLD solid-facet NavIcons set directly and
// never got that fix, so the rail mixed the new thin-stroke primary icons above
// with leftover v16-style filled ones below (Jason 2026-09-17, screenshot of the
// collapsed rail: "half look like v16 leftovers").
const MORE = [
  // v17 cut (docs/v17-scope.md): For You, Canon, Shelves, Buzz, Reviews, Cards dropped.
  { to: '/aftershow',    label: 'Aftershow',     icon: MessagesSquare },
  { to: '/notifications', label: 'Notifications', icon: Bell },
  // History.jsx is a pure redirect (no page of its own) — link straight to its
  // real destination so there's no flicker where the rail's own render condition
  // (surface-wide on the CURRENT page) briefly fails during the bounce.
  { to: '/history', label: 'History', icon: Clock },
  { to: '/import',       label: 'Import',        icon: Download },
  { to: '/settings',     label: 'Settings',      icon: SettingsIcon },
  { to: '/help',         label: 'Help',          icon: CircleHelp },
]

const RAIL_KEY = 'nextep_rail_pinned'

export function DesktopShell({ pathname, profile, cliffUnplayed, faceoffUnplayed, verdictUnplayed, tonightCount, featureFlags }) {
  // App experience gate (2026-09-12): the rail listed every destination regardless of
  // mode, so a Basic account on desktop still saw Cliffhanger/People/Canon/etc. that
  // the mobile hub hid. Same rule as BottomNav: path → feature key → isEnabled.
  const fOn = (to) => { const k = featureKeyForPath((to || '').split('?')[0]); return !k || !featureFlags || featureFlags.isEnabled(k) }
  const navItems  = NAV.filter(i => fOn(i.to))
  const moreItems = MORE.filter(i => fOn(i.to))
  const navigate = useNavigate()
  // 17 (2026-09-15): the rail is a 72px strip of icons that opens to labels on hover,
  // or stays open when pinned. The broadcast chrome (channel numbers, ON AIR clock,
  // SIG/AUX/MON/REC lamps, the activity ticker) is gone — the rail reads in the app's
  // own type now instead of as a different product beside the pages.
  const [pinned, setPinned] = useState(() => { try { return safeStorage.get(RAIL_KEY) === '1' } catch { return false } })
  const togglePin = () => { const n = !pinned; setPinned(n); try { safeStorage.set(RAIL_KEY, n ? '1' : '0') } catch {} }
  const counts = { tonight: tonightCount, cliffhanger: cliffUnplayed, faceoff: faceoffUnplayed, verdict: verdictUnplayed }

  const item = (it, more) => {
    const active = it.to === '/' ? pathname === '/' : pathname.startsWith(it.to.split('?')[0])
    const Icon = it.icon
    const count = it.countKey ? counts[it.countKey] : null
    const showDot = it.dotKey ? !!counts[it.dotKey] : false
    return (
      <button key={it.to} className={`dsk-nav-item${active ? ' is-active' : ''}${more ? ' dsk-nav-item-more' : ''}`}
        onClick={() => { if (active) window.scrollTo({ top: 0, behavior: 'smooth' }); else navigate(it.to) }} aria-current={active ? 'page' : undefined} title={pinned ? undefined : it.label}>
        <span className="dsk-nav-icon"><Icon size={18} strokeWidth={1.8} />{(count > 0 || showDot) && <span className="dsk-nav-pip" aria-hidden />}</span>
        <span className="dsk-nav-label">{it.label}</span>
        {count > 0 && <span className="dsk-nav-badge">{count}</span>}
      </button>
    )
  }

  return (
    <nav className={`dsk-rail${pinned ? ' is-pinned' : ''}`} aria-label="Desktop navigation">
      {/* Logo-as-home: the friction log had rage taps on an unwired logo for weeks
          (2026-09-14 audit). The mark is always visible; the wordmark joins it when
          the rail is open. */}
      <button onClick={() => { if (window.location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' }); else navigate('/') }} aria-label="NextEp home" className="dsk-rail-brand">
        <NextEpLogo size={24} glow={false} />
        <span className="dsk-brand-label"><Wordmark size={19} /></span>
      </button>
      <div className="dsk-nav-list">
        {navItems.map(i => item(i, false))}
        <div className="dsk-nav-more-label"><span className="dsk-nav-label">More</span></div>
        {moreItems.map(i => item(i, true))}
      </div>
      <button className="dsk-rail-pin" onClick={togglePin} aria-pressed={pinned} title={pinned ? 'Collapse the menu' : 'Keep the menu open'}>
        <span className="dsk-nav-icon">{pinned ? <ChevronsLeft size={16} /> : <ChevronsRight size={16} />}</span>
        <span className="dsk-nav-label">{pinned ? 'Collapse' : 'Keep open'}</span>
      </button>
      <button className="dsk-rail-user" onClick={() => navigate('/profile')} title={pinned ? undefined : 'Profile'}>
        <Avatar name={profile?.display_name || profile?.username} avatarUrl={profile?.avatar_url} size={32} />
        <span className="dsk-nav-label" style={{ fontWeight: 600 }}>{profile?.display_name || profile?.username || 'You'}</span>
      </button>
    </nav>
  )
}

// ── DesktopMasthead ──────────────────────────────────────────────────────────
// Per-page header: CH tag + live clock + serif title/subtitle. A page renders
// this inside its own `.dsk-masthead` wide-only block, right where its mobile
// header would otherwise sit (which stays untouched below 720px).
// 17: the CH·LIVE tag and clock are gone with the rest of the broadcast chrome.
export function DesktopMasthead({ title, sub }) {
  return (
    <div className="dsk-masthead">
      <h1 className="dsk-masthead-title">{title}</h1>
      {sub && <p className="dsk-masthead-sub">{sub}</p>}
    </div>
  )
}
